/**
 * Desired Outcome Editor
 *
 * Full editor for a single Desired Outcome. Presents all 7 questions as
 * clearly labeled sections on one page. Auto-saves on field change.
 * Obstacles and steps are managed as editable JSONB arrays.
 *
 * Route: /prioritization/desired-outcomes/:id
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Flag,
  Link2,
  Loader2,
  Plus,
  Save,
  Target,
  Trash2,
  X,
  GripVertical,
} from 'lucide-react';
import {
  useDesiredOutcomeStore,
  type DesiredOutcome,
  type DesiredOutcomeObstacle,
  type DesiredOutcomeStep,
  type ObstacleCategory,
} from '../../stores/prioritization/desiredOutcomeStore';
import { generateUUID } from '../../lib/supabaseSync';
import {
  getConnectionsForSource,
  deleteConnection,
  type MethodologyConnection,
} from '../../lib/methodologyConnectionsService';
import { LinkToItemPanel } from '../../components/methodology/LinkToItemPanel';
import { InlineOpenQuestionsPanel } from '../../components/methodology/InlineOpenQuestionsPanel';
import { listObjectives, type StrategicObjective } from '../../lib/strategyAlignment';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { AppSelect } from '../../components/ui/AppSelect';
import { StrategyConnectivityHelpButton } from '../prioritization/strategy/StrategyConnectivityDocModal';

// ── Question section wrapper ───────────────────────────────────

const OBSTACLE_CATEGORIES: ObstacleCategory[] = [
  'People',
  'Process',
  'Technology',
  'Policy',
  'Budget',
  'Other',
];

function QSection({
  qNum,
  label,
  sublabel,
  children,
}: {
  qNum: string;
  label: string;
  sublabel?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border border-[var(--rule)] overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-3.5 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/60 border-b border-[var(--rule)]">
        <span className="flex-shrink-0 w-10 h-6 rounded bg-[var(--green-dim)] dark:bg-[var(--green)]/40 text-[var(--green)] dark:text-[var(--text-ter)] text-xs font-mono font-bold flex items-center justify-center">
          {qNum}
        </span>
        <div>
          <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{label}</p>
          {sublabel && (
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">{sublabel}</p>
          )}
        </div>
      </div>
      <div className="px-5 py-4 bg-[var(--bg-raised)] dark:bg-[var(--surface)]">{children}</div>
    </section>
  );
}

// ── Textarea ───────────────────────────────────────────────────

function AutoTextarea({
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]/40 text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none leading-relaxed"
    />
  );
}

// ── Evidence level card ────────────────────────────────────────

function EvidenceCard({
  colorClass,
  label,
  hint,
  value,
  onChange,
}: {
  colorClass: string;
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className={`flex-1 min-w-0 border  overflow-hidden ${colorClass}`}>
      <div className="px-3 py-2 border-b border-current/20">
        <p className="text-xs font-bold uppercase tracking-wide">{label}</p>
        <p className="text-xs opacity-70 mt-0.5">{hint}</p>
      </div>
      <div className="p-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
        <AutoTextarea
          value={value}
          onChange={onChange}
          placeholder="Present-tense, sensory language — what you see, hear, read, feel..."
          rows={4}
        />
      </div>
    </div>
  );
}

// ── Obstacle row ───────────────────────────────────────────────

function ObstacleRow({
  obstacle,
  onChange,
  onDelete,
}: {
  obstacle: DesiredOutcomeObstacle;
  onChange: (updated: DesiredOutcomeObstacle) => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <GripVertical className="h-4 w-4 text-[var(--text-ter)] mt-2.5 flex-shrink-0" />
      <AppSelect
        value={obstacle.category}
        onChange={(v) => onChange({ ...obstacle, category: v as ObstacleCategory })}
        accentColor="indigo"
        className="flex-shrink-0 w-32"
        options={OBSTACLE_CATEGORIES.map((c) => ({ value: c, label: c }))}
      />
      <input
        type="text"
        value={obstacle.description}
        onChange={(e) => onChange({ ...obstacle, description: e.target.value })}
        placeholder="Describe the obstacle specifically..."
        className="flex-1 px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]/40 text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
      />
      <button
        onClick={onDelete}
        className="flex-shrink-0 p-2 text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors mt-0.5"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ── Step row ───────────────────────────────────────────────────

function StepRow({
  step,
  index,
  onChange,
  onDelete,
}: {
  step: DesiredOutcomeStep;
  index: number;
  onChange: (updated: DesiredOutcomeStep) => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <GripVertical className="h-4 w-4 text-[var(--text-ter)] mt-2.5 flex-shrink-0" />
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--hover-tint)] text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs font-bold flex items-center justify-center mt-1.5">
        {index + 1}
      </span>
      <input
        type="text"
        value={step.text}
        onChange={(e) => onChange({ ...step, text: e.target.value })}
        placeholder="Describe this step..."
        className="flex-1 px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]/40 text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
      />
      <label className="flex items-center gap-1.5 flex-shrink-0 px-2 py-2 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] cursor-pointer">
        <input
          type="checkbox"
          checked={step.isStepZero}
          onChange={(e) => onChange({ ...step, isStepZero: e.target.checked })}
          className="rounded border-[var(--rule)] text-[var(--green)] dark:text-[var(--text-ter)] focus:ring-green-500"
        />
        Step 0
      </label>
      <label className="flex items-center gap-1.5 flex-shrink-0 px-2 py-2 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] cursor-pointer">
        <input
          type="checkbox"
          checked={step.isThreshold}
          onChange={(e) => onChange({ ...step, isThreshold: e.target.checked })}
          className="rounded border-[var(--rule)] text-[var(--green)] dark:text-[var(--text-ter)] focus:ring-green-500"
        />
        Gate
      </label>
      <button
        onClick={onDelete}
        className="flex-shrink-0 p-2 text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors mt-0.5"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ── Completeness summary ───────────────────────────────────────

const COMPLETION_CHECKS: { key: keyof DesiredOutcome; label: string }[] = [
  { key: 'who', label: 'Who is named (one Who)' },
  { key: 'whatWanted', label: "What's wanted (positive sentence)" },
  { key: 'deeperBenefit', label: 'Deeper benefit captured' },
  { key: 'evidenceSufficient', label: 'Barely Sufficient evidence' },
  { key: 'evidenceTargeted', label: 'Targeted Success evidence' },
  { key: 'evidenceIdeal', label: 'Hopelessly Idealistic evidence' },
  { key: 'obstacles', label: 'At least one obstacle identified' },
  { key: 'ecology', label: 'Ecology check completed' },
  { key: 'steps', label: 'At least one step listed' },
];

function CompletionSummary({ outcome }: { outcome: DesiredOutcome }) {
  const checks = COMPLETION_CHECKS.map((c) => {
    const v = outcome[c.key];
    let done = false;
    if (typeof v === 'string') done = v.trim().length > 0;
    else if (Array.isArray(v)) done = v.length > 0;
    return { ...c, done };
  });
  const done = checks.filter((c) => c.done).length;
  const pct = Math.round((done / checks.length) * 100);

  return (
    <div className="border border-[var(--rule)] p-5 bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Completeness</p>
        <span className="text-sm font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{pct}%</span>
      </div>
      <div className="h-2 bg-[var(--hover-tint)] rounded-full mb-4">
        <div
          className="h-full bg-[var(--green)] dark:bg-[var(--green)] rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {checks.map((c) => (
          <li key={c.key as string} className="flex items-center gap-2 text-xs">
            {c.done
              ? <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)] flex-shrink-0" />
              : <div className="h-3.5 w-3.5 rounded-full border-2 border-[var(--rule)] flex-shrink-0" />
            }
            <span className={c.done ? 'text-[var(--text-sec)] dark:text-[var(--text-sec)]' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'}>
              {c.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Objective selector ─────────────────────────────────────────

function ObjectiveSelector({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const [objectives, setObjectives] = useState<StrategicObjective[]>([]);

  useEffect(() => {
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;
    listObjectives(workspaceId).then(setObjectives);
  }, []);

  return (
    <div className="border border-[var(--slate)] dark:border-[var(--slate)] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border-b border-[var(--slate)] dark:border-[var(--slate)]">
        <Flag className="h-3.5 w-3.5 text-[var(--text-ter)]" />
        <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          Strategic Objective
        </span>
        <span className="text-xs text-[var(--text-sec)] ml-1">— optional</span>
      </div>
      <div className="px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-2">
          Link this Desired Outcome to the Objective it helps specify. Neither requires the other.
        </p>
        <AppSelect
          value={value ?? ''}
          onChange={(v) => onChange(v || null)}
          placeholder="— No objective linked —"
          accentColor="violet"
          options={objectives.map((o) => ({ value: o.id, label: o.name }))}
        />
      </div>
    </div>
  );
}

// ── Linked Items section ───────────────────────────────────────

function LinkedItemsSection({ outcomeId }: { outcomeId: string }) {
  const [connections, setConnections] = useState<MethodologyConnection[]>([]);
  const [showPanel, setShowPanel] = useState(false);

  const load = useCallback(async () => {
    const { data } = await getConnectionsForSource('desired_outcome', outcomeId);
    setConnections(data);
  }, [outcomeId]);

  useEffect(() => { load(); }, [load]);

  async function handleRemove(id: string) {
    await deleteConnection(id);
    setConnections((prev) => prev.filter((c) => c.id !== id));
  }

  const CONNECTION_LABELS: Record<string, string> = {
    informs: 'informs',
    motivates: 'motivates',
    addresses: 'addresses',
    answers: 'answers',
    derives_from: 'derives from',
  };

  return (
    <div className="border border-[var(--slate)] dark:border-[var(--slate)] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border-b border-[var(--slate)] dark:border-[var(--slate)]">
        <div className="flex items-center gap-2">
          <Link2 className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            Linked prioritization items
            {connections.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)] text-[10px] font-bold">
                {connections.length}
              </span>
            )}
          </span>
        </div>
        <button
          onClick={() => setShowPanel((v) => !v)}
          className="flex items-center gap-1 text-xs font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
        >
          {showPanel ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
          {showPanel ? 'Cancel' : 'Link to item'}
        </button>
      </div>
      <div className="px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
        {showPanel && (
          <div className="mb-3">
            <LinkToItemPanel
              sourceEntityType="desired_outcome"
              sourceEntityId={outcomeId}
              onLinked={() => { setShowPanel(false); load(); }}
              onClose={() => setShowPanel(false)}
            />
          </div>
        )}
        {connections.length === 0 && !showPanel && (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] py-1">
            No linked items yet. Link this outcome to the prioritization items it motivates.
          </p>
        )}
        {connections.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {connections.map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] dark:bg-[var(--slate)]/20 dark:border-[var(--slate)]"
              >
                <span className="opacity-60">{CONNECTION_LABELS[c.connectionType] ?? c.connectionType}</span>
                <span className="font-mono text-[10px] opacity-70">{c.targetEntityId.slice(0, 8)}</span>
                <button
                  onClick={() => handleRemove(c.id)}
                  className="opacity-50 hover:opacity-100 transition-opacity leading-none"
                  title="Remove link"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main editor ────────────────────────────────────────────────

export default function DesiredOutcomeEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { outcomes, isLoading, fetchOutcomes, updateOutcome, deleteOutcome } =
    useDesiredOutcomeStore();

  const outcome = outcomes.find((o) => o.id === id);
  const [local, setLocal] = useState<DesiredOutcome | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (outcomes.length === 0) fetchOutcomes();
  }, [outcomes.length, fetchOutcomes]);

  useEffect(() => {
    if (outcome && !local) setLocal(outcome);
  }, [outcome, local]);

  const scheduleAutoSave = useCallback(
    (updated: DesiredOutcome) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        if (!id) return;
        setIsSaving(true);
        setSaveError(null);
        const { success, error } = await updateOutcome(id, updated);
        setIsSaving(false);
        if (!success) setSaveError(error ?? 'Save failed');
        else setLastSaved(new Date());
      }, 800);
    },
    [id, updateOutcome]
  );

  function patch(updates: Partial<DesiredOutcome>) {
    if (!local) return;
    const next = { ...local, ...updates };
    setLocal(next);
    scheduleAutoSave(next);
  }

  async function handleDelete() {
    if (!id) return;
    setIsDeleting(true);
    await deleteOutcome(id);
    navigate('/prioritization/desired-outcomes');
  }

  if (isLoading && !local) {
    return (
      <div className="flex items-center justify-center py-24 text-[var(--text-ter)]">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading...
      </div>
    );
  }

  if (!local) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-[var(--text-ter)] gap-4">
        <AlertCircle className="h-8 w-8" />
        <p className="text-sm">Desired Outcome not found.</p>
        <Link to="/prioritization/desired-outcomes" className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] underline">
          Back to list
        </Link>
      </div>
    );
  }

  return (
    <div className="pb-16">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/prioritization/desired-outcomes"
            className="flex-shrink-0 p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] min-w-0">
            <span>Desired Outcomes</span>
            <ChevronRight className="h-3 w-3 flex-shrink-0" />
            <span className="truncate font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              {local.title || 'Untitled'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StrategyConnectivityHelpButton context="desired_outcome_editor" />
          {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--text-ter)]" />}
          {!isSaving && lastSaved && (
            <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] flex items-center gap-1">
              <Save className="h-3 w-3" />
              Saved
            </span>
          )}
          {saveError && (
            <span className="text-xs text-[var(--coral)] flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {saveError}
            </span>
          )}
          {confirmDelete ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)]">
              <AlertTriangle className="h-3.5 w-3.5 text-[var(--coral)] flex-shrink-0" />
              <span className="text-xs text-[var(--coral)] dark:text-[var(--coral)]">Delete this outcome?</span>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="text-xs font-medium text-[var(--coral)] hover:text-[var(--coral)] underline"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20 transition-colors"
              title="Delete this Desired Outcome"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Title — full width above the two-column layout */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <div className="flex-shrink-0 p-1.5 bg-[var(--green-dim)] dark:bg-[var(--green)]/30">
            <Target className="h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)]" />
          </div>
          <h1 className="text-xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Desired Outcome
          </h1>
        </div>
        <input
          type="text"
          value={local.title}
          onChange={(e) => patch({ title: e.target.value })}
          placeholder="Name this outcome — the initiative or request you're clarifying"
          className="w-full px-0 py-1.5 text-base font-medium border-0 border-b border-[var(--rule)] bg-transparent text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:border-[var(--green)] transition-colors"
        />
      </div>

      {/* Two-column layout: main content + right panel */}
      <div className="flex gap-6 items-start">

        {/* ── Left: main content ─────────────────────────────────── */}
        <div className="flex-1 min-w-0 space-y-6">

      {/* Q1 */}
      <QSection
        qNum="Q1"
        label="Who wants it?"
        sublabel="Exactly one Who — one persona or logically unified group. Overloading Who is the #1 failure mode."
      >
        <input
          type="text"
          value={local.who}
          onChange={(e) => patch({ who: e.target.value })}
          placeholder="e.g. Head of Product · End users of mobile app · Series A investors"
          className="w-full px-3 py-2.5 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]/40 text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <p className="mt-1.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          If you need two Whos, you need two separate Desired Outcomes.
        </p>
      </QSection>

      {/* Q2 */}
      <QSection
        qNum="Q2"
        label="What's wanted?"
        sublabel="Single sentence, positive language, from the Who's point of view."
      >
        <AutoTextarea
          value={local.whatWanted}
          onChange={(v) => patch({ whatWanted: v })}
          placeholder={`State what's wanted, not what you don't want. "I want a full balloon" — not "I don't want it to pop."`}
          rows={2}
        />
      </QSection>

      {/* Q3 */}
      <QSection
        qNum="Q3"
        label="What does it get them?"
        sublabel={"The deeper benefit or meta-outcome. Ask \u201cSo that\u2026?\u201d until it stops changing decisions."}
      >
        <AutoTextarea
          value={local.deeperBenefit ?? ''}
          onChange={(v) => patch({ deeperBenefit: v || null })}
          placeholder="Include emotional benefits — confidence, peace of mind, pride, sense of control."
          rows={3}
        />
      </QSection>

      {/* Q4 — Evidence */}
      <QSection
        qNum="Q4"
        label="Evidence of success"
        sublabel="Present-tense, sensory language (see / hear / read / feel). Three levels prevent hallucination."
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <EvidenceCard
            colorClass="border-[var(--mustard)] dark:border-[var(--mustard)]/60 bg-[var(--mustard-dim)]/30 dark:bg-[var(--mustard)]/10 text-[var(--mustard)] dark:text-[var(--mustard)]"
            label="Barely Sufficient"
            hint="Truly good enough to deliver — don't set too low"
            value={local.evidenceSufficient ?? ''}
            onChange={(v) => patch({ evidenceSufficient: v || null })}
          />
          <EvidenceCard
            colorClass="border-[var(--green)] dark:border-[var(--green)]/60 bg-[var(--green-dim)]/30 dark:bg-[var(--green)]/10 text-[var(--green)] dark:text-[var(--text-ter)]"
            label="Targeted Success"
            hint="Ambitious yet plausible — what we're aiming for"
            value={local.evidenceTargeted ?? ''}
            onChange={(v) => patch({ evidenceTargeted: v || null })}
          />
          <EvidenceCard
            colorClass="border-[var(--slate)] dark:border-[var(--slate)]/60 bg-[var(--slate-dim)]/30 dark:bg-[var(--slate)]/10 text-[var(--text-ter)] dark:text-[var(--text-ter)]"
            label="Hopelessly Idealistic"
            hint="Remove all constraints — what would be amazing?"
            value={local.evidenceIdeal ?? ''}
            onChange={(v) => patch({ evidenceIdeal: v || null })}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1.5">
              Soonest needed
            </label>
            <input
              type="text"
              value={local.soonestNeeded ?? ''}
              onChange={(e) => patch({ soonestNeeded: e.target.value || null })}
              placeholder="When do you actually need to start using it?"
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]/40 text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1.5">
              Last responsible moment
            </label>
            <input
              type="text"
              value={local.lastResponsibleMoment ?? ''}
              onChange={(e) => patch({ lastResponsibleMoment: e.target.value || null })}
              placeholder="Latest possible delivery without causing problems?"
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]/40 text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </QSection>

      {/* Q5 — Obstacles */}
      <QSection
        qNum="Q5"
        label="Obstacles to success"
        sublabel="What prevents it now? Categorize each obstacle — be specific, not 'it's hard'."
      >
        <div className="space-y-2">
          {local.obstacles.map((o) => (
            <ObstacleRow
              key={o.id}
              obstacle={o}
              onChange={(updated) =>
                patch({ obstacles: local.obstacles.map((x) => (x.id === o.id ? updated : x)) })
              }
              onDelete={() =>
                patch({ obstacles: local.obstacles.filter((x) => x.id !== o.id) })
              }
            />
          ))}
        </div>
        <button
          onClick={() =>
            patch({
              obstacles: [
                ...local.obstacles,
                { id: generateUUID(), category: 'People', description: '' },
              ],
            })
          }
          className="mt-3 flex items-center gap-1.5 text-sm text-[var(--green)] hover:text-[var(--green)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add obstacle
        </button>
      </QSection>

      {/* Q6 — Ecology */}
      <QSection
        qNum="Q6"
        label="Unintended consequences — Ecology Check"
        sublabel={"Who loses? What breaks? What compounds over time? Don\u2019t accept \u201cnone\u201d \u2014 there are always tradeoffs."}
      >
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1.5">
              Negative consequences
            </label>
            <AutoTextarea
              value={local.ecology ?? ''}
              onChange={(v) => patch({ ecology: v || null })}
              placeholder="Who loses if we get this? What could break? What risks accumulate over time?"
              rows={3}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1.5">
              Mitigations
            </label>
            <AutoTextarea
              value={local.ecologyMitigations ?? ''}
              onChange={(v) => patch({ ecologyMitigations: v || null })}
              placeholder="How do we prevent or reduce each identified risk?"
              rows={2}
            />
          </div>
        </div>
      </QSection>

      {/* Q7 — Steps */}
      <QSection
        qNum="Q7"
        label="Steps to the desired outcome"
        sublabel="Sequence with dependencies and go/no-go gates. Look for Step Zero — the hidden precondition."
      >
        <div className="space-y-2">
          {local.steps.map((s, i) => (
            <StepRow
              key={s.id}
              step={s}
              index={i}
              onChange={(updated) =>
                patch({ steps: local.steps.map((x) => (x.id === s.id ? updated : x)) })
              }
              onDelete={() =>
                patch({ steps: local.steps.filter((x) => x.id !== s.id) })
              }
            />
          ))}
        </div>
        <button
          onClick={() =>
            patch({
              steps: [
                ...local.steps,
                { id: generateUUID(), text: '', isStepZero: false, isThreshold: false },
              ],
            })
          }
          className="mt-3 flex items-center gap-1.5 text-sm text-[var(--green)] hover:text-[var(--green)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add step
        </button>
        <p className="mt-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <strong>Step 0</strong> = a hidden precondition (get permission, secure budget, validate assumption).{' '}
          <strong>Gate</strong> = go/no-go decision point tied to an evidence level.
        </p>
      </QSection>

          <CompletionSummary outcome={local} />

        </div>{/* end left column */}

        {/* ── Right: linked objects panel ───────────────────────── */}
        <div className="w-72 flex-shrink-0 space-y-4 sticky top-6 self-start">

          {/* Linked to Strategic Objective */}
          <ObjectiveSelector
            value={local.objectiveId}
            onChange={(objectiveId) => patch({ objectiveId })}
          />

          {/* Linked prioritization items */}
          <LinkedItemsSection outcomeId={id!} />

          {/* Open questions anchored to this outcome */}
          <InlineOpenQuestionsPanel
            entityType="desired_outcome"
            entityId={id!}
            accentColor="green"
          />

        </div>{/* end right panel */}

      </div>{/* end two-column layout */}
    </div>
  );
}
